/**
 * useGrantCatalog (Story 32-3 — Epic 32 Multi-Account UX)
 *
 * Data layer for the CatalogPickerDialog and the `connect()` multi-tenant
 * pre-flight, over the Story 32-3 server surface:
 *
 *   GET  /api/integrations/catalog/:platform   (read model)
 *   POST /api/integrations/catalog/refresh     (on-demand 31-24 unit)
 *   POST /api/integrations/catalog/attach      (created/existing/failed)
 *
 * Client-side types mirror the server's grant-catalog.ts response shapes —
 * the server JSDoc is canonical (house pattern, 32-1 precedent).
 *
 * `fetchGrantCatalog` is a plain (non-hook) fetch so `useIntegrationConnect`
 * can run the AC #1 pre-flight inside its `connect()` callback without
 * dragging TanStack Query context into that hook.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost } from "../lib/apiClient";

// ============================================================================
// Types (server response mirror)
// ============================================================================

export interface GrantCatalogAttachedClient {
  clientId: string;
  clientName: string;
  integrationId: string;
}

export interface GrantCatalogResource {
  resourceId: string;
  externalAccountId: string;
  externalAccountName: string | null;
  resourceStatus: string;
  attachedClients: GrantCatalogAttachedClient[];
}

export interface GrantCatalogGrant {
  oauthTokenId: string;
  providerAccountName: string | null;
  providerAccountId: string | null;
  tokenStatus: string;
  /** Cross-platform MAX(last_discovered_at); null = never discovered. */
  lastDiscoveredAt: number | null;
  resources: GrantCatalogResource[];
}

export interface GrantCatalogData {
  platform: string;
  oauthGrantScope: "single-tenant" | "multi-tenant";
  grants: GrantCatalogGrant[];
}

export interface CatalogAttachmentInput {
  resourceId: string;
  clientId: string;
}

export interface CatalogAttachResult {
  created: Array<{ integrationId: string; resourceId: string; clientId: string }>;
  existing: Array<{ integrationId: string; resourceId: string; clientId: string }>;
  failed: Array<{ resourceId: string; clientId: string; errorCode: string }>;
}

export interface CatalogRefreshSummary {
  tokenId: string; // SHA-256-truncated by the server — never the raw id
  status: "discovered" | "skipped_unhealthy" | "skipped_fresh";
  failures: Array<{ tokenId: string; platform: string; errorCode: string }>;
}

// ============================================================================
// Constants + helpers
// ============================================================================

/** Mirror of the server's 6h freshness guard — the AC #8 staleness signal. */
export const CATALOG_STALE_MS = 6 * 60 * 60 * 1000;

/**
 * Synthetic-row prefix (mirror of LEGACY_RESOURCE_PREFIX in
 * apps/server/src/services/grant-resource-catalog.ts). The server already
 * excludes `__legacy_` rows from the GET — this is the picker's DEFENSIVE
 * display filter only (synthetic placeholders are never user-attachable).
 */
export const LEGACY_RESOURCE_PREFIX = "__legacy_";

/**
 * Token statuses whose grants the picker treats as usable (mirror of the
 * cron's DISCOVERABLE_TOKEN_STATUSES — status vocabulary, not a provider
 * list).
 */
export const USABLE_TOKEN_STATUSES: ReadonlySet<string> = new Set([
  "healthy",
  "expiring_soon",
]);

export function grantCatalogQueryKey(platform: string) {
  return ["grant-catalog", platform] as const;
}

/** True when ≥1 grant can serve the picker (the AC #1 pre-flight decision). */
export function hasUsableGrant(catalog: GrantCatalogData): boolean {
  return catalog.grants.some((grant) =>
    USABLE_TOKEN_STATUSES.has(grant.tokenStatus)
  );
}

/** AC #8: a grant is stale when never discovered or older than the 6h guard. */
export function isGrantStale(grant: GrantCatalogGrant, now = Date.now()): boolean {
  return (
    grant.lastDiscoveredAt === null ||
    now - grant.lastDiscoveredAt > CATALOG_STALE_MS
  );
}

/** Plain fetch (non-hook) — shared by the picker query and the pre-flight. */
export async function fetchGrantCatalog(
  platform: string
): Promise<GrantCatalogData> {
  const res = await apiGet(`/api/integrations/catalog/${platform}`);
  if (!res.ok) {
    throw new Error(`Failed to load catalog (${res.status})`);
  }
  const json = (await res.json()) as { data: GrantCatalogData };
  return json.data;
}

/** Thrown by the refresh mutation on the 60s per-token cooldown (429). */
export class CatalogRefreshCooldownError extends Error {
  constructor() {
    super("Catalog was refreshed moments ago");
    this.name = "CatalogRefreshCooldownError";
  }
}

// ============================================================================
// Hook
// ============================================================================

interface UseGrantCatalogArgs {
  /** Platform whose catalog the picker shows; null = picker closed. */
  platform: string | null;
  /** Gate the query — only fetch while the picker is open. */
  enabled: boolean;
  /**
   * Fired after a successful attach (≥1 created) — the consumer's
   * integration-list refetch, matching useIntegrationConnect's
   * `onIntegrationChange` convention.
   */
  onIntegrationChange?: () => void;
}

export function useGrantCatalog({
  platform,
  enabled,
  onIntegrationChange,
}: UseGrantCatalogArgs) {
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: grantCatalogQueryKey(platform ?? "__none__"),
    queryFn: () => fetchGrantCatalog(platform!),
    enabled: enabled && !!platform,
    // v5 API (`keepPreviousData: true` is gone) — keeps the resource list
    // rendered while a refresh-triggered refetch is in flight.
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    if (platform) {
      void queryClient.invalidateQueries({
        queryKey: grantCatalogQueryKey(platform),
      });
    }
  };

  const refreshMutation = useMutation({
    mutationFn: async (input: { oauthTokenId: string; force?: boolean }) => {
      const res = await apiPost("/api/integrations/catalog/refresh", {
        oauthTokenId: input.oauthTokenId,
        ...(input.force ? { force: true } : {}),
      });
      if (res.status === 429) {
        throw new CatalogRefreshCooldownError();
      }
      if (!res.ok) {
        throw new Error(`Refresh failed (${res.status})`);
      }
      const json = (await res.json()) as { data: CatalogRefreshSummary };
      return json.data;
    },
    onSettled: invalidate,
  });

  const attachMutation = useMutation({
    mutationFn: async (attachments: CatalogAttachmentInput[]) => {
      const res = await apiPost("/api/integrations/catalog/attach", {
        platform,
        attachments,
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as {
          detail?: string;
        } | null;
        throw new Error(detail?.detail ?? `Attach failed (${res.status})`);
      }
      const json = (await res.json()) as { data: CatalogAttachResult };
      return json.data;
    },
    onSuccess: (data) => {
      if (data.created.length > 0) {
        onIntegrationChange?.();
      }
    },
    onSettled: invalidate,
  });

  // Story 32-4 AC #6/#7: the picker's remap mode confirms through PATCH
  // /api/integrations/:id/remap instead of the attach POST. Error detail is
  // the RFC 9457 Problem Details `detail` field (toast verbatim).
  const remapMutation = useMutation({
    mutationFn: async (input: {
      integrationId: string;
      oauthGrantResourceId: string;
    }) => {
      const res = await apiPatch(
        `/api/integrations/${input.integrationId}/remap`,
        { oauthGrantResourceId: input.oauthGrantResourceId }
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as {
          detail?: string;
        } | null;
        throw new Error(detail?.detail ?? `Remap failed (${res.status})`);
      }
      const json = (await res.json()) as {
        data: { remapped: boolean; integrationId: string; oauthGrantResourceId: string };
      };
      return json.data;
    },
    onSuccess: () => onIntegrationChange?.(),
    onSettled: invalidate,
  });

  return { catalogQuery, refreshMutation, attachMutation, remapMutation };
}
